/**
 * Pure helpers for the Admin Sales & Tax dashboard.
 *
 * Keep everything in this module free of React, fetch, and env access
 * so it can be tested with the project's existing Jest + jsdom setup
 * (no @testing-library dependency required).
 */

/** Format an integer cents value to a "$X.YY" string. `null`/`NaN`
 * intentionally return "Data incomplete" so unavailable metrics are
 * never rendered as $0.00. */
export function formatCents(cents) {
  if (cents == null || Number.isNaN(cents)) return "Data incomplete";
  return "$" + (cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

/** Human-readable label used every time the raw bucket name is
 * literally "unknown". Kept in one place so the Admin UI can never
 * accidentally hide the bucket. */
export const UNKNOWN_LABEL = "Unknown (not captured)";

/** Given a `by_method` object, return rows sorted by customer_total
 * desc. `unknown` rows are preserved verbatim — never dropped, never
 * merged with a real bucket. */
export function groupByMethodToRows(byMethod, labelKey = "method") {
  if (!byMethod || typeof byMethod !== "object") return [];
  return Object.entries(byMethod)
    .map(([k, v]) => ({ [labelKey]: k, ...v }))
    .sort((a, b) => (b.customer_total || 0) - (a.customer_total || 0));
}

/** Reconciliation invariant asserted by the backend
 * (customer_total ≡ net_merch + tax + shipping). Returns
 * `{ ok: boolean, drift: int_cents }` — drift is the signed gap so
 * the UI can surface exactly how far off the totals are, if ever. */
export function reconcileSummary(m) {
  if (!m) return { ok: false, drift: 0 };
  const lhs = m.customer_total || 0;
  const rhs = (m.net_merchandise_sales || 0) + (m.tax_collected || 0) + (m.shipping_charged || 0);
  return { ok: lhs === rhs, drift: lhs - rhs };
}

/** True iff a metric key must render "Data incomplete" until the
 * underlying schema/pipeline is extended. Kept in one place so we
 * can never accidentally start reporting $0 for a metric we don't
 * yet track. */
export const UNAVAILABLE_METRIC_KEYS = new Set(["discount_total"]);

export function isUnavailableMetric(key) {
  return UNAVAILABLE_METRIC_KEYS.has(key);
}

/** Trend bucket total assembly: sum bucket customer_totals + unbucketed
 * check. Returns the invariant the tests + UI must both preserve. */
export function trendBucketReconcile(trendResponse) {
  if (!trendResponse) return { ok: false, bucketed: 0, unbucketed: 0, totalOrders: 0 };
  const bucketed = (trendResponse.buckets || []).reduce((n, b) => n + (b.orders || 0), 0);
  const unbucketed = trendResponse.unbucketed_orders || 0;
  const total = trendResponse.totals?.orders || 0;
  return { ok: bucketed + unbucketed === total, bucketed, unbucketed, totalOrders: total };
}
